/**
 * Runtime API configuration helpers.
 *
 * Problem this solves:
 * - In dev, if you open the frontend from another device (phone) using the LAN IP,
 *   `http://localhost:8000` points to the *phone*, not your dev machine.
 * - If VITE_API_URL is not set, we derive the backend host from the current page host.
 * - If VITE_API_URL is set to http://localhost:8000/api (e.g. from env.example) but you open
 *   the site via http://192.168.x.x:5173, we ignore that env and use the same host as the page
 *   on port 8000 — otherwise every API call still targets localhost on the client device.
 */

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

/** True if the configured API base URL points to this machine only (loopback). */
function apiUrlRefersToLoopback(apiUrl: string): boolean {
  try {
    const normalized = /^https?:\/\//i.test(apiUrl) ? apiUrl : `http://${apiUrl}`
    const u = new URL(normalized)
    return isLoopbackHostname(u.hostname)
  } catch {
    return /\b(localhost|127\.0\.0\.1)\b/i.test(apiUrl)
  }
}

export function getApiBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim()
  const pageIsLan =
    typeof window !== 'undefined' && !isLoopbackHostname(window.location.hostname)

  if (fromEnv && !(pageIsLan && apiUrlRefersToLoopback(fromEnv))) {
    return fromEnv.replace(/\/+$/, '')
  }

  if (typeof window === 'undefined') return 'http://localhost:8000/api'

  const { protocol, hostname } = window.location
  return `${protocol}//${hostname}:8000/api`.replace(/\/+$/, '')
}

export function getApiVersion(): string {
  return ((import.meta.env.VITE_API_VERSION as string | undefined) || 'v1').trim() || 'v1'
}

/**
 * Get the backend base URL (without /api suffix) for media files.
 * In dev, this is typically http://localhost:8000
 */
export function getBackendBaseUrl(): string {
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim()
  const pageIsLan =
    typeof window !== 'undefined' && !isLoopbackHostname(window.location.hostname)

  if (fromEnv && !(pageIsLan && apiUrlRefersToLoopback(fromEnv))) {
    return fromEnv.replace(/\/api\/?$/, '').replace(/\/+$/, '')
  }
  if (typeof window === 'undefined') return 'http://localhost:8000'
  const { protocol, hostname } = window.location
  return `${protocol}//${hostname}:8000`.replace(/\/+$/, '')
}

/**
 * Normalize photo image URL to point to the backend server or keep external URLs (e.g. B2).
 * - Relative media URLs (/media/, /static/) → prepend backend base.
 * - Frontend assets (/src/, /assets/, /icons/, /img/) → keep on app origin (Vite).
 * - Absolute URLs (https://...) → keep as-is (e.g. Backblaze B2, CDN).
 */
export function normalizePhotoUrl(imageUrl: string | null | undefined): string | null {
  if (imageUrl == null || typeof imageUrl !== 'string') return null
  const s = imageUrl.trim()
  if (!s) return null
  // Already absolute (B2, CDN, localhost) → do not rewrite
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) {
    return s
  }
  // Vite / public frontend assets must not be rewritten to the API host
  if (/^\/(src|assets|icons|img)\//.test(s)) {
    return s
  }
  const backendBase = getBackendBaseUrl()
  // Backend-served media / static
  if (s.startsWith('/')) {
    return `${backendBase}${s}`
  }
  // Path without leading slash → treat as relative to backend
  return `${backendBase}/${s}`
}